import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  LogOut, ArrowLeft, Undo2, Redo2, Trash2, Download, Save,
  Square, Circle, Minus, Droplets, Thermometer, AlertTriangle,
  Move, Pencil, Type, ZoomIn, ZoomOut, RotateCcw, Layers,
  ChevronDown, X, Check, Pipette
} from 'lucide-react';

const LOGO = '/kardif-logo.png';

// Symboles métier pour les fuites et investigations
const SYMBOLS = [
  { id: 'fuite', icon: '💧', name: 'Fuite détectée', color: '#3B82F6' },
  { id: 'humidite', icon: '💦', name: 'Zone humide', color: '#06B6D4' },
  { id: 'infiltration', icon: '🌊', name: 'Infiltration', color: '#0EA5E9' },
  { id: 'origine', icon: '⭕', name: 'Origine probable', color: '#EF4444' },
  { id: 'mesure', icon: '📍', name: 'Point de mesure', color: '#F59E0B' },
  { id: 'thermique', icon: '🌡️', name: 'Anomalie thermique', color: '#DC2626' },
  { id: 'danger', icon: '⚠️', name: 'Zone à risque', color: '#F97316' },
  { id: 'ok', icon: '✅', name: 'Zone OK', color: '#10B981' },
];

const COLORS = ['#1A1A1A', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#8B7355'];
const STROKE_WIDTHS = [2, 4, 6, 8, 12];

function DessinSchema() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tool, setTool] = useState('pencil'); // pencil, line, rect, circle, symbol, text, move
  const [color, setColor] = useState('#1A1A1A');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [selectedSymbol, setSelectedSymbol] = useState(SYMBOLS[0]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [elements, setElements] = useState([]);
  const [currentElement, setCurrentElement] = useState(null);
  const [showSymbols, setShowSymbols] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      redrawCanvas();
    }
  }, []);

  // Redraw canvas when elements change
  useEffect(() => {
    redrawCanvas();
  }, [elements, zoom, pan, backgroundImage]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw grid
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    const gridSize = 20 * zoom;
    for (let x = pan.x % gridSize; x < canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = pan.y % gridSize; y < canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    // Draw background image if exists
    if (backgroundImage) {
      ctx.globalAlpha = 0.3;
      ctx.drawImage(backgroundImage, pan.x, pan.y, backgroundImage.width * zoom, backgroundImage.height * zoom);
      ctx.globalAlpha = 1;
    }
    
    // Draw all elements
    elements.forEach(el => drawElement(ctx, el));
  }, [elements, zoom, pan, backgroundImage]);

  const drawElement = (ctx, el) => {
    ctx.save();
    ctx.strokeStyle = el.color;
    ctx.fillStyle = el.color;
    ctx.lineWidth = el.strokeWidth || 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (el.type) {
      case 'pencil':
        if (el.points && el.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(el.points[0].x * zoom + pan.x, el.points[0].y * zoom + pan.y);
          el.points.forEach((p, i) => {
            if (i > 0) ctx.lineTo(p.x * zoom + pan.x, p.y * zoom + pan.y);
          });
          ctx.stroke();
        }
        break;
      case 'line':
        ctx.beginPath();
        ctx.moveTo(el.x1 * zoom + pan.x, el.y1 * zoom + pan.y);
        ctx.lineTo(el.x2 * zoom + pan.x, el.y2 * zoom + pan.y);
        ctx.stroke();
        break;
      case 'rect':
        ctx.strokeRect(
          el.x * zoom + pan.x, el.y * zoom + pan.y,
          el.width * zoom, el.height * zoom
        );
        break;
      case 'circle':
        ctx.beginPath();
        ctx.arc(
          el.x * zoom + pan.x, el.y * zoom + pan.y,
          el.radius * zoom, 0, Math.PI * 2
        );
        ctx.stroke();
        break;
      case 'symbol':
        ctx.font = `${24 * zoom}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(el.icon, el.x * zoom + pan.x, el.y * zoom + pan.y);
        // Draw label
        ctx.font = `${10 * zoom}px Arial`;
        ctx.fillStyle = el.color;
        ctx.fillText(el.label || '', el.x * zoom + pan.x, (el.y + 20) * zoom + pan.y);
        break;
      case 'text':
        ctx.font = `${(el.fontSize || 14) * zoom}px Arial`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(el.text, el.x * zoom + pan.x, el.y * zoom + pan.y);
        break;
    }
    ctx.restore();
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom
    };
  };

  const handleStart = (e) => {
    e.preventDefault();
    const pos = getCanvasCoords(e);
    setIsDrawing(true);

    if (tool === 'text') {
      setTextPosition(pos);
      return;
    }

    if (tool === 'symbol') {
      const newElement = {
        type: 'symbol',
        x: pos.x,
        y: pos.y,
        icon: selectedSymbol.icon,
        color: selectedSymbol.color,
        label: selectedSymbol.name
      };
      addElement(newElement);
      return;
    }

    const newElement = {
      type: tool,
      color,
      strokeWidth,
      ...(tool === 'pencil' ? { points: [pos] } : {}),
      ...(tool === 'line' ? { x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y } : {}),
      ...(tool === 'rect' ? { x: pos.x, y: pos.y, width: 0, height: 0 } : {}),
      ...(tool === 'circle' ? { x: pos.x, y: pos.y, radius: 0 } : {}),
    };
    setCurrentElement(newElement);
  };

  const handleMove = (e) => {
    if (!isDrawing || !currentElement) return;
    e.preventDefault();
    const pos = getCanvasCoords(e);

    let updated = { ...currentElement };
    switch (tool) {
      case 'pencil':
        updated.points = [...updated.points, pos];
        break;
      case 'line':
        updated.x2 = pos.x;
        updated.y2 = pos.y;
        break;
      case 'rect':
        updated.width = pos.x - updated.x;
        updated.height = pos.y - updated.y;
        break;
      case 'circle':
        updated.radius = Math.sqrt(Math.pow(pos.x - updated.x, 2) + Math.pow(pos.y - updated.y, 2));
        break;
    }
    setCurrentElement(updated);

    // Live preview
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    redrawCanvas();
    drawElement(ctx, updated);
  };

  const handleEnd = () => {
    if (currentElement) {
      addElement(currentElement);
    }
    setIsDrawing(false);
    setCurrentElement(null);
  };

  const addElement = (el) => {
    const newElements = [...elements, el];
    setElements(newElements);
    // Save to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newElements);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(history[historyIndex - 1]);
    } else if (historyIndex === 0) {
      setHistoryIndex(-1);
      setElements([]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(history[historyIndex + 1]);
    }
  };

  const clearCanvas = () => {
    if (window.confirm('Effacer tout le dessin ?')) {
      setElements([]);
      setHistory([[]]);
      setHistoryIndex(0);
    }
  };

  const handleTextSubmit = () => {
    if (textInput && textPosition) {
      addElement({
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: textInput,
        color,
        fontSize: strokeWidth * 3
      });
    }
    setTextInput('');
    setTextPosition(null);
  };

  const loadBackgroundImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const img = new Image();
      img.onload = () => setBackgroundImage(img);
      img.src = URL.createObjectURL(file);
    }
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `schema_${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const getBackPath = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'secretary') return '/secretary';
    return '/dashboard';
  };

  const tools = [
    { id: 'pencil', icon: Pencil, name: 'Crayon' },
    { id: 'line', icon: Minus, name: 'Ligne' },
    { id: 'rect', icon: Square, name: 'Rectangle' },
    { id: 'circle', icon: Circle, name: 'Cercle' },
    { id: 'symbol', icon: Droplets, name: 'Symboles' },
    { id: 'text', icon: Type, name: 'Texte' },
  ];

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col touch-none">
      {/* Header */}
      <header className="bg-black/90 backdrop-blur-lg border-b border-[#8B7355]/30 safe-area-top">
        <div className="px-2 sm:px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={LOGO} alt="KARDIF" className="h-8 sm:h-10" />
              <div className="hidden sm:block">
                <h1 className="text-white font-semibold text-sm">Schémas Investigation</h1>
                <p className="text-[#8B7355] text-xs">Plans et annotations</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button variant="ghost" size="sm" onClick={undo} disabled={historyIndex < 0} className="text-white p-2">
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={redo} disabled={historyIndex >= history.length - 1} className="text-white p-2">
                <Redo2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={clearCanvas} className="text-red-400 p-2">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadCanvas} className="text-green-400 p-2">
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => nav(getBackPath())} className="text-white p-2">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="bg-[#2D2D2D] border-b border-[#8B7355]/20 px-2 py-2 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {/* Tools */}
          {tools.map((t) => (
            <Button
              key={t.id}
              variant="ghost"
              size="sm"
              onClick={() => {
                setTool(t.id);
                if (t.id === 'symbol') setShowSymbols(true);
              }}
              className={`p-2 ${tool === t.id ? 'bg-[#8B7355] text-white' : 'text-white/70 hover:text-white'}`}
              title={t.name}
            >
              <t.icon className="h-5 w-5" />
            </Button>
          ))}
          
          <div className="w-px h-6 bg-white/20 mx-1" />
          
          {/* Color picker */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowColors(!showColors)}
            className="p-2 text-white/70"
          >
            <div className="w-5 h-5 rounded-full border-2 border-white" style={{ backgroundColor: color }} />
          </Button>
          
          {/* Stroke width */}
          <select 
            value={strokeWidth} 
            onChange={(e) => setStrokeWidth(Number(e.target.value))}
            className="bg-[#3D3D3D] text-white text-sm rounded px-2 py-1 border-0"
          >
            {STROKE_WIDTHS.map(w => (
              <option key={w} value={w}>{w}px</option>
            ))}
          </select>
          
          <div className="w-px h-6 bg-white/20 mx-1" />
          
          {/* Zoom */}
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} className="text-white/70 p-2">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-white text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="text-white/70 p-2">
            <ZoomIn className="h-4 w-4" />
          </Button>
          
          <div className="w-px h-6 bg-white/20 mx-1" />
          
          {/* Load background */}
          <label className="cursor-pointer">
            <input type="file" accept="image/*" onChange={loadBackgroundImage} className="hidden" />
            <div className="flex items-center gap-1 text-white/70 text-sm px-2 py-1 hover:text-white">
              <Layers className="h-4 w-4" />
              <span className="hidden sm:inline">Fond</span>
            </div>
          </label>
        </div>
      </div>

      {/* Color picker popup */}
      {showColors && (
        <div className="absolute top-24 left-2 bg-[#2D2D2D] rounded-lg p-2 shadow-xl z-50 border border-[#8B7355]/30">
          <div className="grid grid-cols-4 gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setColor(c); setShowColors(false); }}
                className="w-8 h-8 rounded-full border-2 border-white/30 hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Symbols popup */}
      {showSymbols && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-[#2D2D2D] rounded-xl p-4 shadow-xl z-50 border border-[#8B7355]/30 w-[90vw] max-w-md">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-white font-semibold">Symboles Investigation</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowSymbols(false)} className="text-white/70">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SYMBOLS.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelectedSymbol(s); setShowSymbols(false); setTool('symbol'); }}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${selectedSymbol.id === s.id ? 'bg-[#8B7355]' : 'bg-[#3D3D3D] hover:bg-[#4D4D4D]'}`}
              >
                <span className="text-2xl">{s.icon}</span>
                <span className="text-white text-sm">{s.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Text input popup */}
      {textPosition && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2D2D2D] rounded-xl p-4 shadow-xl z-50 border border-[#8B7355]/30 w-[90vw] max-w-sm">
          <h3 className="text-white font-semibold mb-3">Ajouter du texte</h3>
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            className="w-full bg-[#3D3D3D] text-white rounded-lg px-4 py-3 border-0 mb-3"
            placeholder="Votre texte..."
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setTextInput(''); setTextPosition(null); }} className="flex-1">
              Annuler
            </Button>
            <Button onClick={handleTextSubmit} className="flex-1 bg-[#8B7355] hover:bg-[#7A6548] text-white">
              Ajouter
            </Button>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div ref={containerRef} className="flex-1 overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={window.innerWidth}
          height={window.innerHeight - 140}
          className="touch-none cursor-crosshair"
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      </div>

      {/* Bottom quick symbols for mobile */}
      <div className="bg-[#2D2D2D] border-t border-[#8B7355]/20 px-2 py-2 safe-area-bottom sm:hidden">
        <div className="flex justify-around">
          {SYMBOLS.slice(0, 5).map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelectedSymbol(s); setTool('symbol'); }}
              className={`p-2 rounded-lg ${selectedSymbol.id === s.id && tool === 'symbol' ? 'bg-[#8B7355]' : ''}`}
            >
              <span className="text-xl">{s.icon}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DessinSchema;
